/**
 * "Start an access request" — the Access card, in the conversation (6 Oct).
 *
 * With the access front door on, the assistant does not hand over the form of
 * a user-access offering (New User Request, New User IT Setup, Business
 * Systems Access, CRM V11, Deactivate): it streams an `access_card` instead,
 * and this card opens "Request access for a person" with what the
 * conversation already said — the person, the site, the start date, the
 * systems. WHY (product owner's UAT): "set up a new user in Lagrange, Tom
 * Mann" was looked up in the directory and then got the retired IT Setup form.
 *
 * Someone leaving (6 Oct): the card opens the wizard on User deactivation,
 * with the leaver and the leaving date (`start_date`) when the chat knew them.
 *
 * 28 Oct (Rusty): the HelpDesk starts Change access only. A New user or a
 * Leaver shows "HR starts this in the HR Portal" and no button.
 *
 * Type unclear (7 Oct, product owner: "i need to get a user crm"): the card
 * comes with no type and opens the wizard on its first question (New hire /
 * User change request / User deactivation) with nothing picked, and the
 * systems that were named already ticked.
 */

import { ChevronRight, Icon, UserPlus } from "@norican/icons";
import { Button, Card, cn, DescriptionList, Typography } from "@norican/react";
import { useId } from "react";
import { Link } from "react-router";

import { useDateFormatter } from "@/components/common";
import { accessWizardPath, helpdeskStartTypes, HrPortalHint } from "@/features/access";
import { useT } from "@/i18n";

import type { AssistantAccessCardEvent } from "./contract";

export interface AccessCardProps {
  card: AssistantAccessCardEvent;
  className?: string;
}

export function AccessCard({ card, className }: AccessCardProps) {
  const { t } = useT("assistant");
  const { t: tt } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const headingId = useId();
  const leave = card.access_type === "leave";
  // 28 Oct (Rusty): the HelpDesk starts Change access only (as the server
  // says); a New user or a Leaver gets the HR Portal line and no button. No
  // type known: the card opens the wizard on what the HelpDesk starts.
  const starts = helpdeskStartTypes(card.helpdesk_starts);
  const hrOnly = card.access_type != null && !starts.includes(card.access_type);
  const openType = card.access_type == null && starts.length === 1 ? starts[0] : card.access_type;

  const items = [
    card.person_name ? { label: t("accessCard.person"), value: card.person_name } : null,
    card.location ? { label: t("accessCard.location"), value: card.location } : null,
    card.start_date
      ? {
          label: leave ? t("accessCard.leavingDate") : t("accessCard.startDate"),
          value: formatDate(new Date(`${card.start_date}T12:00:00`)),
        }
      : null,
    (card.systems ?? []).length > 0
      ? {
          label: t("accessCard.systems"),
          value: (card.systems ?? [])
            .map((key) => tt(`access.item.${key}` as never, { defaultValue: key }))
            .join(", "),
        }
      : null,
  ].filter((item): item is { label: string; value: string } => item !== null);

  const path = accessWizardPath({
    type: openType,
    askType: openType == null,
    name: card.person_name,
    personId: card.person_id,
    startDate: card.start_date,
    location: card.location,
    items: card.systems,
  });

  return (
    <section aria-labelledby={headingId} className={cn("w-full", className)}>
      <Card variant="outline" accent="left" accentTone="accent">
        <Card.Header>
          <div className="flex min-w-0 items-start gap-3">
            <Icon icon={UserPlus} size="md" tone="accent" />
            <div className="flex min-w-0 flex-col gap-1">
              <Typography as="h3" variant="heading-5" id={headingId}>
                {hrOnly ? tt(`access.type.${card.access_type ?? "new"}`) : t("accessCard.title")}
              </Typography>
              {hrOnly ? (
                // 28 Oct (Rusty): one line, no button: HR starts it in the HR Portal.
                <HrPortalHint type={card.access_type} url={card.hr_portal_url} />
              ) : (
                <Typography variant="body-sm" tone="muted">
                  {t(`accessCard.body.${openType ?? "choose"}`)}
                </Typography>
              )}
            </div>
          </div>
        </Card.Header>
        {hrOnly ? null : (
          <Card.Body>
            <div className="flex flex-col gap-4">
              {items.length === 0 ? null : (
                <DescriptionList items={items} orientation="horizontal" />
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Button
                  endIcon={<Icon icon={ChevronRight} size="sm" />}
                  render={<Link to={path} />}
                >
                  {t("accessCard.open")}
                </Button>
              </div>
            </div>
          </Card.Body>
        )}
      </Card>
    </section>
  );
}

export interface AccessCardListProps {
  cards: AssistantAccessCardEvent[];
  className?: string;
}

export function AccessCardList({ cards, className }: AccessCardListProps) {
  if (cards.length === 0) {
    return null;
  }
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {cards.map((card) => (
        <AccessCard key={JSON.stringify(card)} card={card} />
      ))}
    </div>
  );
}
